// apps/client/src/services/comment.ts
import {
  CommentCreateResponse,
  CommentListResponse,
  CommentUpdateResponse,
  CommentWithUser,
  CommentWithContext,
  CommentableType,
} from '@fsp/types';
import { apiClient } from './apiClient';

/**
 * Builds the base URL for comment operations based on entity type
 */
const getCommentBaseUrl = (
  organisationId: string,
  planId: string,
  type: CommentableType,
  entityId: string
): string => {
  switch (type) {
    case 'page':
      // For pages, we need the chapterId - extract from entityId format "chapterId:pageId"
      // or pass it separately
      throw new Error('Use listPageComments for page comments');
    case 'hazard':
      return `/organisations/${organisationId}/plans/${planId}/hazards/${entityId}/comments`;
    case 'ccp':
      return `/organisations/${organisationId}/plans/${planId}/ccps/${entityId}/comments`;
    default:
      throw new Error(`Unsupported commentable type: ${type}`);
  }
};

/**
 * List all comments for a page
 */
export const listPageComments = async (
  organisationId: string,
  planId: string,
  chapterId: string,
  pageId: string,
  signal?: AbortSignal
): Promise<CommentWithUser[]> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/plans/${planId}/chapters/${chapterId}/pages/${pageId}/comments`,
    signal
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch page comments: ${response.statusText}`);
  }

  const result: CommentListResponse = await response.json();
  return result.comments || [];
};

/**
 * List all comments for a hazard
 */
export const listHazardComments = async (
  organisationId: string,
  planId: string,
  hazardId: string,
  signal?: AbortSignal
): Promise<CommentWithUser[]> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/plans/${planId}/hazards/${hazardId}/comments`,
    signal
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch hazard comments: ${response.statusText}`);
  }

  const result: CommentListResponse = await response.json();
  return result.comments || [];
};

/**
 * List all comments for a CCP
 */
export const listCCPComments = async (
  organisationId: string,
  planId: string,
  ccpId: string,
  signal?: AbortSignal
): Promise<CommentWithUser[]> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/plans/${planId}/ccps/${ccpId}/comments`,
    signal
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch CCP comments: ${response.statusText}`);
  }

  const result: CommentListResponse = await response.json();
  return result.comments || [];
};

/**
 * Create a new comment for a page
 */
export const createPageComment = async (
  organisationId: string,
  planId: string,
  chapterId: string,
  pageId: string,
  content: string,
  signal?: AbortSignal
): Promise<CommentWithUser> => {
  const response = await apiClient.post(
    `/organisations/${organisationId}/plans/${planId}/chapters/${chapterId}/pages/${pageId}/comments`,
    { content },
    signal
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to create page comment: ${response.statusText}`);
  }

  const result: CommentCreateResponse = await response.json();
  return result.comment;
};

/**
 * Create a new comment for a hazard
 */
export const createHazardComment = async (
  organisationId: string,
  planId: string,
  hazardId: string,
  content: string,
  signal?: AbortSignal
): Promise<CommentWithUser> => {
  const response = await apiClient.post(
    `/organisations/${organisationId}/plans/${planId}/hazards/${hazardId}/comments`,
    { content },
    signal
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(
      errorResult.message || `Failed to create hazard comment: ${response.statusText}`
    );
  }

  const result: CommentCreateResponse = await response.json();
  return result.comment;
};

/**
 * Create a new comment for a CCP
 */
export const createCCPComment = async (
  organisationId: string,
  planId: string,
  ccpId: string,
  content: string,
  signal?: AbortSignal
): Promise<CommentWithUser> => {
  const response = await apiClient.post(
    `/organisations/${organisationId}/plans/${planId}/ccps/${ccpId}/comments`,
    { content },
    signal
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to create CCP comment: ${response.statusText}`);
  }

  const result: CommentCreateResponse = await response.json();
  return result.comment;
};

/**
 * Update an existing comment (author only)
 * For supplier comments, pass empty string for planId
 */
export const updateComment = async (
  organisationId: string,
  planId: string,
  commentId: string,
  content: string,
  commentType?: CommentableType,
  entityId?: string,
  signal?: AbortSignal
): Promise<CommentWithUser> => {
  let url: string;

  // For supplier comments, use supplier-specific route
  if (commentType === 'supplier' && entityId) {
    url = `/organisations/${organisationId}/suppliers/${entityId}/comments/${commentId}`;
  } else {
    url = `/organisations/${organisationId}/plans/${planId}/comments/${commentId}`;
  }

  const response = await apiClient.put(url, { content }, signal);

  if (!response.ok) {
    let errorMessage = `Failed to update comment: ${response.statusText}`;
    try {
      const errorResult = await response.json();
      errorMessage = errorResult.message || errorMessage;
    } catch {
      // Response is not JSON, use statusText
    }
    throw new Error(errorMessage);
  }

  const result: CommentUpdateResponse = await response.json();
  return result.comment;
};

/**
 * Delete a comment (author or admin)
 * For supplier comments, pass empty string for planId
 */
export const deleteComment = async (
  organisationId: string,
  planId: string,
  commentId: string,
  commentType?: CommentableType,
  entityId?: string,
  signal?: AbortSignal
): Promise<void> => {
  let url: string;

  // For supplier comments, use supplier-specific route
  if (commentType === 'supplier' && entityId) {
    url = `/organisations/${organisationId}/suppliers/${entityId}/comments/${commentId}`;
  } else {
    url = `/organisations/${organisationId}/plans/${planId}/comments/${commentId}`;
  }

  const response = await apiClient.delete(url, signal);

  if (!response.ok) {
    let errorMessage = `Failed to delete comment: ${response.statusText}`;
    try {
      const errorResult = await response.json();
      errorMessage = errorResult.message || errorMessage;
    } catch {
      // Response is not JSON, use statusText
    }
    throw new Error(errorMessage);
  }
};

/**
 * List all comments for a supplier
 */
export const listSupplierComments = async (
  organisationId: string,
  supplierId: string,
  signal?: AbortSignal
): Promise<CommentWithUser[]> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/suppliers/${supplierId}/comments`,
    signal
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch supplier comments: ${response.statusText}`);
  }

  const result: CommentListResponse = await response.json();
  return result.comments || [];
};

/**
 * Create a new comment for a supplier
 */
export const createSupplierComment = async (
  organisationId: string,
  supplierId: string,
  content: string,
  signal?: AbortSignal
): Promise<CommentWithUser> => {
  const response = await apiClient.post(
    `/organisations/${organisationId}/suppliers/${supplierId}/comments`,
    { content },
    signal
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(
      errorResult.message || `Failed to create supplier comment: ${response.statusText}`
    );
  }

  const result: CommentCreateResponse = await response.json();
  return result.comment;
};

/**
 * List recent comments for an organisation (max 10)
 * Returns comments with navigation context (planId, chapterId, etc.)
 */
export const listOrganisationRecentComments = async (
  organisationId: string,
  signal?: AbortSignal
): Promise<CommentWithContext[]> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/comments`,
    signal
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch organisation comments: ${response.statusText}`);
  }

  const result: CommentListResponse = await response.json();
  return result.comments as CommentWithContext[] || [];
};
